<script setup lang="ts">
import type { NovaCheckboxProps } from './NovaCheckbox.types'

withDefaults(defineProps<NovaCheckboxProps>(), {
  isError: false,
  label: '',
  direction: 'row',
  theme: 'default',
  readonly: false,
})
</script>

<template>
  <div
    :class="[
      'checkbox',
      `dir-${direction}`,
      { checked: isCheck, readonly, error: isError },
    ]"
  >
    <NovaIcon
      v-if="theme === 'default'"
      class="icon"
      :icon="{
        type: 'outline',
        name: isCheck ? 'checkbox-check' : 'checkbox-uncheck',
      }"
    />

    <div v-if="theme === 'circle'" class="circle-checkbox">
      <NovaIcon
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="16"
      />
    </div>

    <div v-if="theme === 'square'" class="square-checkbox">
      <NovaIcon
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="16"
      />
    </div>

    <div v-if="theme === 'circle-xl'" class="circle-xl-checkbox">
      <NovaIcon
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="30"
      />
    </div>

    <div v-if="theme === 'circle-lg'" class="circle-lg-checkbox">
      <NovaIcon
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="24"
      />
    </div>

    <div v-if="theme === 'circle-md'" class="circle-md-checkbox">
      <NovaIcon
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="24"
      />
    </div>

    <div v-if="theme === 'circle-md-20'" class="circle-md-checkbox type-black">
      <NovaIcon
        v-if="isCheck"
        class="icon"
        :icon="{
          type: 'outline',
          name: 'check',
        }"
        :size="20"
      />
    </div>

    <span
      class="label"
      :style="{ fontSize: fontSize, fontWeight: fontWeight }"
      >{{ label }}</span
    >
  </div>
</template>

<style lang="scss">
.checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;

  &.readonly {
    pointer-events: none;
  }

  &.dir-row {
    flex-direction: row;
  }
  &.dir-column {
    flex-direction: column;
  }
  &.dir-row-reverse {
    flex-direction: row-reverse;
  }
  &.dir-column-reverse {
    flex-direction: column-reverse;
  }

  &.checked {
    > .circle-checkbox {
      color: $color-secondary-green-light-90;
      border-color: $color-secondary-green-light-90;
    }
    > .square-checkbox {
      color: $color-bg-3;
      border-color: $color-dark-1;
      background-color: $color-dark-1;
    }
    > .circle-md-checkbox,
    > .circle-md-checkbox.type-black,
    > .circle-xl-checkbox,
    > .circle-lg-checkbox {
      color: $color-bg-3;
      border-color: $color-blue-700;
      background-color: $color-blue-700;
    }
  }

  &.error {
    .icon {
      &:before {
        color: $color-primary-red;
      }
    }
  }

  > .square-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid $color-text-10;
  }

  > .circle-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 10px;
    border: 2px solid $color-text-10;
  }

  > .circle-xl-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-neutral-dark-4;
    width: 38px;
    height: 38px;
    border-radius: 19px;
    border: 2px solid $color-neutral-dark-4;

    @include mobile {
      width: 30px;
      height: 30px;

      :deep(.icon) {
        font-size: 20px !important;
      }
    }
  }

  > .circle-lg-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-neutral-dark-4;
    width: 30px;
    height: 30px;
    border-radius: 15px;
    border: 2px solid $color-neutral-dark-4;

    @include mobile {
      width: 24px;
      height: 24px;

      :deep(.icon) {
        font-size: 16px !important;
      }
    }
  }

  > .circle-md-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-neutral-dark-4;
    width: 24px;
    height: 24px;
    margin-left: 4px;
    border-radius: 12px;
    border: 2px solid $color-neutral-dark-4;
    &.type-black {
      width: 20px;
      height: 20px;
      margin-left: 0;
      border: 2px solid $color-text-12;
    }
    @include mobile {
      width: 20px;
      height: 20px;

      :deep(.icon) {
        font-size: 12px !important;
      }
    }
  }

  > .label {
    margin-left: 4px;
    line-height: 1.4;
    word-break: break-all;
  }

  > .circle-xl-checkbox + .label {
    margin-left: 13px;
    @include text-style($text-custom-22-bold);

    @include mobile {
      @include text-style($text-display-bold);
    }
  }
  > .circle-lg-checkbox + .label {
    margin-left: 18px;
    @include text-style($text-heading2-medium);

    @include mobile {
      @include text-style($text-body-14-medium);
    }
  }
  > .circle-md-checkbox + .label {
    margin-left: 18px;
    @include text-style($text-heading2-medium);

    @include mobile {
      @include text-style($text-body-14-medium);
    }
  }
}
</style>
